<!-- Resources.vue - CLEAN MODERN DESIGN -->
<template>
  <div class="container mx-auto p-4 sm:p-6">
    <h1 class="text-3xl sm:text-4xl font-bold mb-8 text-slate-900 dark:text-white text-center">Resources</h1>

    <!-- Accordion for Resources -->
    <div class="space-y-3">
      <div
          v-for="(section, index) in sections"
          :key="index"
          class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-md rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 transition-all duration-200"
          :class="section.isOpen ? 'shadow-lg' : ''"
      >
        <button
            @click="toggleSection(index)"
            class="w-full text-left px-5 py-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 flex justify-between items-center transition-colors duration-200"
        >
          <span class="text-lg font-semibold text-slate-900 dark:text-white">{{ section.title }}</span>
          <svg
              :class="{'transform rotate-180': section.isOpen}"
              class="w-5 h-5 text-slate-500 dark:text-slate-400 transition-transform duration-200"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div v-show="section.isOpen" class="bg-white dark:bg-slate-900 px-5 py-5 border-t border-slate-200 dark:border-slate-700">
          <!-- List of Resources -->
          <ul class="space-y-3">
            <li v-for="(resource, idx) in section.resources" :key="idx" class="flex items-start gap-3">
              <svg class="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              <div class="flex-1">
                <component
                    :is="getResourceComponent(resource)"
                    v-bind="getResourceProps(resource)"
                    class="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium transition-colors"
                >
                  {{ resource.title }}
                </component>
                <p v-if="resource.description" class="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {{ resource.description }}
                </p>
              </div>
            </li>
          </ul>

          <!-- Additional content for Library News -->
          <div v-if="section.news" class="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            <h3 class="text-xl font-bold mb-4 text-slate-900 dark:text-white">News</h3>
            <ul class="space-y-5">
              <li v-for="(newsItem, idx) in section.news" :key="idx">
                <p class="text-slate-900 dark:text-white font-semibold mb-2">{{ newsItem.title }}</p>
                <div v-for="(content, cIdx) in newsItem.contents" :key="cIdx">
                  <p v-if="content.text" class="text-slate-600 dark:text-slate-400 mb-2">{{ content.text }}</p>
                  <ul v-if="content.links" class="space-y-2">
                    <li v-for="(link, lIdx) in content.links" :key="lIdx" class="flex items-start gap-3">
                      <svg class="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <a
                          :href="link.url"
                          target="_blank"
                          rel="noreferrer"
                          class="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm transition-colors"
                      >
                        {{ link.title }}
                      </a>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useSchoolContext } from '@/config/schools';

const { activeSchoolId } = useSchoolContext();

const resourceSectionsBySchool = {
  collegiate: [
    {
      title: 'Lunch Menu',
      resources: [
        {
          title: 'View Current Lunch Menu',
          link: 'https://collegiateschool.flikisdining.com/menu/dining-hall/lunch/',
        },
      ],
    },
    {
      title: 'Library',
      resources: [
        {
          title: 'Library Homepage',
          link: 'https://www.collegiateschool.org/academics/library',
          description: 'Hours: 7:30am - 5:00pm Monday - Friday',
        },
        {
          title: 'Sora Collection',
          link: 'https://soraapp.com/library/hvlany',
        },
        {
          title: 'Lower School Databases',
          link: 'https://libguides.collegiateschool.org/lowerschooldatabases',
          description: 'Password: collegiateschoollibrary',
        },
        {
          title: 'Middle/Upper School Databases',
          link: 'https://libguides.collegiateschool.org/middleupperschooldatabases',
          description: 'Password: collegiateschoollibrary',
        },
        {
          title: 'Catalog',
          link: 'http://follett.collegiateschool.org/',
        },
        {
          title: 'Book Request Form',
          link: 'https://forms.gle/KUp1CboZTKwz1TmT8',
        },
      ],
      news: [
        {
          title: 'Sora Tutorials',
          contents: [
            {
              links: [
                {
                  title: 'Sora Login',
                  url: 'https://youtu.be/RIXicV3Z8yE',
                },
                {
                  title: 'Sora Non-School Account Login',
                  url: 'https://youtu.be/al5MyUqLTro',
                },
                {
                  title: 'Sora Tour',
                  url: 'https://youtu.be/0JyIlMzi3bc',
                },
                {
                  title: 'Sora: Adding a Library Account',
                  url: 'https://youtu.be/sRcmLeWuJSI',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      title: 'Important Links and Resources',
      resources: [
        {
          title: '301 Freedom Place South Floor Plan',
          link: 'https://collegiateschool.myschoolapp.com/ftpimages/37/download/download_2299406.pdf?_=1731119376018',
        },
        {
          title: 'Calendar Year-at-a-Glance',
          link: 'https://collegiateschoolorg.finalsite.com/fs/resource-manager/view/28ef1a25-0aea-4042-96cf-7991d2bfd711',
        },
        {
          title: 'Allergy Policy',
          link: 'https://www.collegiateschool.org/fs/resource-manager/view/d17d613c-73a1-4b63-a47b-f7bc0c0e3dcf',
        },
        {
          title: 'Lost Card Report/Request for New Card',
          link: 'https://collegiateschool.myschoolapp.com/ftpimages/37/download/download_group6783_id373769.pdf?_=1731119376022',
        },
      ],
    },
    {
      title: 'Upper School Downloads',
      resources: [
        {
          title: 'US Handbook',
          link: 'https://www.collegiateschool.org/fs/resource-manager/view/79c69241-52f2-4f5a-8552-1d78245da292',
        },
        {
          title: 'Upper School Databases',
          link: 'https://libguides.collegiateschool.org/middleupperschooldatabases',
          description: 'Password: collegiateschoollibrary',
        },
        {
          title: 'Digital US Schedule',
          link: 'https://collegiateschool.myschoolapp.com/ftpimages/37/download/download_5910958.pdf',
        },
        {
          title: 'Acceptable Use Policy',
          link: 'https://collegiateschool.myschoolapp.com/ftpimages/37/download/download_1661857.pdf?_=1731119376022',
        },
        {
          title: 'The Dread of Writing Essays and Papers',
          link: 'https://collegiateschool.myschoolapp.com/ftpimages/37/download/download_group6783_id173024.pdf?_=1731119376022',
        },
        {
          title: 'Fall 2023 Course Guide',
          link: 'https://collegiateschool.myschoolapp.com/ftpimages/37/download/download_7098497.pdf?_=1731119376022',
        },
      ],
    },
    {
      title: 'Middle School Downloads',
      resources: [
        {
          title: 'MS Handbook',
          link: 'https://www.collegiateschool.org/fs/resource-manager/view/6e46850f-ac13-4103-9b5f-27b3670136c8',
        },
        {
          title: 'Middle School Databases',
          link: 'https://libguides.collegiateschool.org/middleupperschooldatabases',
          description: 'Password: collegiateschoollibrary',
        },
        {
          title: 'Acceptable Use Policy',
          link: 'https://collegiateschool.myschoolapp.com/ftpimages/37/download/download_1661857.pdf?_=1731119376021',
        },
      ],
    },
    {
      title: 'Athletics Downloads',
      resources: [
        {
          title: 'Exemption Form Grades 7-12',
          link: 'https://collegiateschool.myschoolapp.com/ftpimages/37/download/download_1316131.pdf?_=1731119376021',
        },
        {
          title: 'Athletics Unaccompanied Dismissal Form',
          link: 'https://collegiateschool.myschoolapp.com/ftpimages/37/download/download_2137942.pdf?_=1731119376021',
          description: 'For off-campus athletic events that finish after school, parents may request permission for a student\'s dismissal from the event location.',
        },
        {
          title: 'Preseason Soccer',
          link: 'https://collegiateschool.myschoolapp.com/ftpimages/37/download/download_8248770.pdf?_=1731119376021',
          description: 'Preseason training camp for students interested in trying out for JV/Varsity soccer.',
        },
        {
          title: 'Cross Country Training Camp',
          link: 'https://collegiateschool.myschoolapp.com/ftpimages/37/download/download_8248766.pdf?_=1731119376021',
          description: 'Camp Sloane is the preseason training camp for students interested in trying out for cross country.',
        },
        {
          title: 'Athletics Handbook',
          link: 'https://www.collegiateschool.org/fs/resource-manager/view/5c1b2768-8d42-483d-afd0-994e94fe9f74',
        },
      ],
    },
  ],
  grace: [
    {
      title: 'Daily Essentials',
      resources: [
        {
          title: 'Lunch Menu',
          to: '/menu',
          description: 'Open BetterConnect\'s in-app dining menu for Grace Church School.',
        },
        {
          title: 'Key Dates',
          link: 'https://www.gcschool.org/quicklinks/key-dates',
          description: 'Grace Church School\'s official academic calendar and major dates.',
        },
        {
          title: 'Directions & Contact',
          link: 'https://www.gcschool.org/quicklinks/directions--contact',
        },
        {
          title: 'GraceNet Login Help',
          link: 'https://www.gcschool.org/gracenet-login-help',
        },
      ],
    },
    {
      title: 'Library',
      resources: [
        {
          title: 'Libraries',
          link: 'https://www.gcschool.org/academics/libraries',
          description: 'Library hours, staff, research support, and collection details.',
        },
        {
          title: 'Grace Church School Databases',
          link: 'https://gcschool.libguides.com',
        },
        {
          title: 'Stokes Library Online',
          link: 'https://gcschool.follettdestiny.com',
        },
        {
          title: 'High School Library/Information Center',
          link: 'https://gcschoolny.booksys.net',
        },
      ],
    },
    {
      title: 'High School',
      resources: [
        {
          title: 'Program & Schedules',
          link: 'https://www.gcschool.org/academics/high-school/program--schedules',
        },
        {
          title: 'Four-Year Course of Study',
          link: 'https://www.gcschool.org/academics/high-school/program--schedules/four-year-course-of-study',
        },
        {
          title: 'Lab Day Schedule',
          link: 'https://www.gcschool.org/academics/high-school/program--schedules/lab-day-schedule',
        },
        {
          title: 'Lab Studies',
          link: 'https://www.gcschool.org/academics/high-school/lab-studies',
        },
      ],
    },
    {
      title: 'Athletics',
      resources: [
        {
          title: 'Athletics Overview',
          link: 'https://www.gcschool.org/programs/athletics',
        },
        {
          title: 'Griffins Team Schedules',
          link: 'https://www.gcschool.org/griffins-teams',
        },
        {
          title: 'Varsity Sports',
          link: 'https://www.gcschool.org/programs/athletics/varsity-sports',
        },
        {
          title: 'Junior Varsity Sports',
          link: 'https://www.gcschool.org/programs/athletics/junior-varsity-sports',
        },
        {
          title: 'Middle School Sports',
          link: 'https://www.gcschool.org/programs/athletics/middle-school-sports',
        },
      ],
    },
  ],
};

function cloneSection(section) {
  return {
    ...section,
    isOpen: false,
    resources: section.resources.map((resource) => ({ ...resource })),
    news: section.news?.map((newsItem) => ({
      ...newsItem,
      contents: newsItem.contents.map((content) => ({
        ...content,
        links: content.links?.map((link) => ({ ...link })),
      })),
    })),
  };
}

function buildSectionsForSchool(schoolId) {
  const sectionDefinitions = resourceSectionsBySchool[schoolId] ?? resourceSectionsBySchool.collegiate;
  return sectionDefinitions.map(cloneSection);
}

export default {
  name: 'Resources',
  data() {
    return {
      sections: [],
    };
  },
  computed: {
    schoolId() {
      return activeSchoolId.value;
    },
  },
  watch: {
    schoolId: {
      immediate: true,
      handler() {
        this.sections = buildSectionsForSchool(this.schoolId);
      },
    },
  },
  methods: {
    toggleSection(index) {
      this.sections[index].isOpen = !this.sections[index].isOpen;
    },
    getResourceComponent(resource) {
      return resource.to ? 'router-link' : 'a';
    },
    getResourceProps(resource) {
      if (resource.to) {
        return { to: resource.to };
      }

      return {
        href: resource.link,
        target: '_blank',
        rel: 'noreferrer',
      };
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
